import React from 'react'
import PropTypes from 'prop-types'
import DisImage from '../../components/cms/image'
import AspectRatio from '../../components/cms/aspect-ratio'

/**
 * Image component for media module that renders responsive images with aspect ratio containers.
 * Automatically crops images using object-fit: cover to prevent distortion across different screen sizes.
 *
 * @param {Object} props - Component props
 * @param {Object} props.asset - Image asset object containing src and metadata
 * @param {string} props.asset.src - Image source URL
 * @param {string} [props.asset.ariaLabel] - ARIA label for accessibility
 * @param {string} [props.asset.alt] - Alt text for the image
 * @param {string} [props.asset.mobileSrc] - Mobile-specific image source (legacy, not used in current implementation)
 * @param {'landscape'|'portrait'} props.variant - Display variant that affects mobile aspect ratio
 * @returns {JSX.Element} AspectRatio container with responsive DisImage
 */
const Image = ({asset, variant}) => {
    const {src, ariaLabel, alt} = asset

    // Responsive aspect ratio: portrait uses 4:5 on mobile, landscape uses 16:9 on mobile, both use 16:9 on desktop
    const ratio = {base: variant === 'portrait' ? 4 / 5 : 16 / 9, s: 16 / 9}

    return (
        <AspectRatio ratio={ratio}>
            <DisImage
                aria-label={ariaLabel}
                src={src}
                alt={alt}
                objectFit="cover"
                widths={{base: '100vw', xs: '100vw', s: '100vw'}}
            />
        </AspectRatio>
    )
}

Image.propTypes = {
    asset: PropTypes.shape({
        // Image source URL - used for both mobile and desktop in current implementation
        src: PropTypes.string.isRequired,
        ariaLabel: PropTypes.string,
        alt: PropTypes.string
    }).isRequired,
    variant: PropTypes.oneOf(['landscape', 'portrait']).isRequired
}

export default Image
